'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';

interface WebSocketMessage {
  type: string;
  content?: string;
  error?: string;
  message?: string;
  data?: any;
  timestamp?: string;
  engines_used?: any[];
}

interface ChatMessage {
  id: string;
  type: 'user' | 'bot' | 'error' | 'system';
  content: string;
  timestamp: string;
  sender: 'user' | 'bot' | 'system';
  engines_used?: any[];
}

interface WebSocketContextType {
  // 状态
  socket: WebSocket | null;
  isConnected: boolean;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  lastMessage: WebSocketMessage | null;
  messages: ChatMessage[];
  error: string | null;
  configFile: string;

  // 聊天功能
  sendMessage: (content: string, metadata?: any) => { success: boolean; error?: string; data?: any };
  sendSystemMessage: (type: string, content: string, data?: any) => { success: boolean; error?: string; data?: any };

  // 项目订阅功能
  subscribeProject: (projectId: number) => { success: boolean; error?: string };
  unsubscribeProject: (projectId: number) => { success: boolean; error?: string };

  // 未来功能占位
  sendNotification: (title: string, message: string, type?: string) => { success: boolean; error?: string };
  subscribeProgress: (taskId: string, callback: Function) => Function;
  joinCollaboration: (sessionId: string) => { success: boolean; error?: string };

  // 连接管理
  connect: () => Promise<{ success: boolean; error?: string }>;
  disconnect: () => void;
  onMessage: (callback: (message: WebSocketMessage) => void) => Function;
  onStatusChange: (callback: (status: string) => void) => Function;

  // 工具方法
  clearMessages: () => void;
  clearError: () => void;
  getConnectionInfo: () => {
    status: string;
    connected: boolean;
    configFile: string;
    messageCount: number;
    lastActivity: string | null;
    reconnectAttempts: number;
  };

  // 任务进度回调
  onTaskProgress: (callback: (data: any) => void) => Function;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export function useWebSocket(): WebSocketContextType {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

interface WebSocketProviderProps {
  children: React.ReactNode;
}

// 全局连接状态，避免React StrictMode导致的重复连接
const globalConnectionRef = {
  isConnecting: false,
  isInitialized: false
};

// WebSocket状态转换为可读字符串
function getWebSocketStateString(readyState: number): string {
  switch (readyState) {
    case 0: return 'CONNECTING';
    case 1: return 'OPEN';
    case 2: return 'CLOSING';
    case 3: return 'CLOSED';
    default: return 'UNKNOWN';
  }
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [configFile, setConfigFile] = useState('project01.yaml');

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;
  const messageCallbacksRef = useRef(new Map<number, (message: WebSocketMessage) => void>());
  const statusChangeCallbacksRef = useRef(new Map<number, (status: string) => void>());
  const taskProgressCallbacksRef = useRef(new Map<number, (data: any) => void>());

  // WebSocket配置
  const getWebSocketUrl = useCallback((): string => {
    // 生成唯一的客户端ID
    const clientId = `frontend_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    //🔧 Windows 临时方案：直接连接后端，绕过 Vite 代理
    if (window.location.hostname === 'localhost' && navigator.platform.includes('Win')) {
      console.log('🪟 检测到 Windows 系统，使用直连模式');
      return `ws://127.0.0.1:8001/ws/chat?client_id=${clientId}`;
    }

    // 其他系统：使用 Vite 代理
    const host = window.location.hostname === 'localhost'
      ? `${window.location.host}`
      : window.location.host;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    // 开发环境使用相对路径，生产环境使用完整路径
    const wsPath = window.location.hostname === 'localhost'
      ? `/ws/chat?client_id=${clientId}`
      : `${protocol}//${host}/ws/chat?client_id=${clientId}`;

    return wsPath;
  }, []);

  // 连接WebSocket
  const connect = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    try {
      // 全局重复连接检查，防止React StrictMode导致的重复连接
      if (globalConnectionRef.isConnecting) {
        // 跳过重复连接
        return { success: true };
      }

      // 避免重复连接
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          // 跳过重复连接
          return { success: true };
        } else if (wsRef.current.readyState === WebSocket.CONNECTING) {
          // 跳过重复连接
          globalConnectionRef.isConnecting = true;
          return { success: true };
        }
      }

      setConnectionStatus('connecting');
      setError(null);

      // 关闭现有连接
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      const wsUrl = getWebSocketUrl();
      console.log('🚀 [WebSocket] 开始连接...', wsUrl);
      
      const ws = new WebSocket(wsUrl);

      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ [WebSocket] 连接成功!', wsUrl);
        setIsConnected(true);
        setConnectionStatus('connected');
        reconnectAttemptsRef.current = 0;
        setConfigFile('default');

        // 通知所有状态变化回调
        statusChangeCallbacksRef.current.forEach(callback => {
          try {
            callback('connected');
          } catch (error) {
            console.error('状态变化回调处理错误:', error);
          }
        });
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          setLastMessage(message);

          // 处理不同类型的消息
          handleMessageByType(message);

          // 通知所有消息回调
          messageCallbacksRef.current.forEach(callback => {
            try {
              callback(message);
            } catch (error) {
              console.error('消息回调处理错误:', error);
            }
          });

        } catch (error) {
          console.error('解析WebSocket消息失败:', error);
        }
      };

      ws.onclose = (event: CloseEvent) => {
        console.log('🔴 [WebSocket] 连接关闭', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          url: wsUrl
        });
        setIsConnected(false);
        setConnectionStatus('disconnected');
        setSocket(null);

        // 通知所有状态变化回调
        statusChangeCallbacksRef.current.forEach(callback => {
          try {
            callback('disconnected');
          } catch (error) {
            console.error('状态变化回调处理错误:', error);
          }
        });

        // 自动重连（但避免在短时间内重复连接）
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = reconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1); // 指数退避
          setTimeout(() => connect(), delay);
        }
      };

      ws.onerror = (errorEvent: Event) => {
        console.error('❌ [WebSocket] 连接错误', { url: wsUrl, readyState: ws.readyState });
        setConnectionStatus('error');
        setError('WebSocket连接错误');

        // 通知所有状态变化回调
        statusChangeCallbacksRef.current.forEach(callback => {
          try {
            callback('error');
          } catch (error) {
            console.error('状态变化回调处理错误:', error);
          }
        });
      };

      setSocket(ws);
      return { success: true };
    } catch (error) {
      setError('WebSocket连接失败');
      setConnectionStatus('error');
      setIsConnected(false);
      return { success: false, error: 'WebSocket连接失败' };
    }
  }, [configFile, getWebSocketUrl]);

  // 断开连接
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setSocket(null);
    setIsConnected(false);
    setConnectionStatus('disconnected');
  }, []);

  // 根据消息类型处理
  const handleMessageByType = useCallback((message: WebSocketMessage) => {
    switch (message.type) {
      case 'response':
      case 'message.completed':
        // 只处理最终的聊天回复消息，但不在这里添加到消息列表，让组件自己处理
        // 这样可以避免在两个地方都添加消息导致重复
        break;

      case 'error':
        // 错误消息
        setMessages(prev => [...prev, {
          id: `msg_${Date.now()}`,
          type: 'error',
          content: message.error || '发生错误',
          timestamp: new Date().toISOString(),
          sender: 'system'
        }]);
        break;

      case 'heartbeat':
      case 'pong':
      case 'ping':
        // 心跳消息，静默处理，不显示给用户
        break;

      case 'processing.started':
      case 'chatbot.created':
      case 'team.created':
      case 'message.received':
        // 系统状态消息，不在聊天界面显示
        // 这些消息会通过回调函数传递给需要监听状态的组件
        if (message.data || message.payload) {
          // 通知所有消息回调，让组件自己处理系统状态更新
          messageCallbacksRef.current.forEach(callback => {
            try {
              callback(message);
            } catch (error) {
              console.error('系统状态更新回调处理错误:', error);
            }
          });
        }
        break;

      case 'system':
        // 系统消息 - 只在重要时显示
        if (message.message && message.message.trim()) {
          setMessages(prev => [...prev, {
            id: `msg_${Date.now()}`,
            type: 'system',
            content: message.message || '',
            timestamp: new Date().toISOString(),
            sender: 'system'
          }]);
        }
        break;

      // TODO: 未来功能扩展 - 实时通知
      case 'notification':
        // 这是未来实时通知需要实现
        // 处理实时通知消息
        break;

      // 进度更新
      case 'progress':
      case 'step.progress':
        // 处理任务进度更新
        // 这些消息会通过回调传递给订阅的组件
        if (message.data || message.payload) {
          // 通知所有消息回调，让组件自己处理进度更新
          messageCallbacksRef.current.forEach(callback => {
            try {
              callback(message);
            } catch (error) {
              console.error('进度更新回调处理错误:', error);
            }
          });
        }
        break;

      // TODO: 未来功能扩展 - 协作功能
      case 'collaboration':
        // 这是未来协作功能需要实现
        // 处理多用户协作消息
        break;

      // 项目更新消息
      case 'project_update':
        // 处理项目状态更新，包括报告生成进度
        // 这些消息会通过回调传递给订阅的组件
        // 确保消息被正确传递给所有注册的回调函数
        if (message.data || message.payload) {
          // 通知所有消息回调，让组件自己处理项目更新
          messageCallbacksRef.current.forEach(callback => {
            try {
              callback(message);
            } catch (error) {
              console.error('项目更新回调处理错误:', error);
            }
          });
        }
        break;

      // 报告生成进度
      case 'report_progress':
        // 处理报告生成进度更新
        // 这些消息会通过回调传递给订阅的组件
        // 确保消息被正确传递给所有注册的回调函数
        if (message.data || message.payload) {
          // 通知所有消息回调，让组件自己处理进度更新
          messageCallbacksRef.current.forEach(callback => {
            try {
              callback(message);
            } catch (error) {
              console.error('报告进度回调处理错误:', error);
            }
          });
        }
        break;

      // 任务进度更新
      case 'task_progress':
        // 处理任务进度更新
        // 通知所有任务进度回调
        taskProgressCallbacksRef.current.forEach(callback => {
          try {
            callback(message.data || message);
          } catch (error) {
            console.error('任务进度回调处理错误:', error);
          }
        });
        break;

      default:
        // 未知消息类型，静默处理，不产生错误
        break;
    }
  }, []);

  // 发送聊天消息
  const sendMessage = useCallback((content: string, metadata: any = {}): { success: boolean; error?: string; data?: any } => {
    if (!isConnected || !wsRef.current) {
      setError('WebSocket未连接');
      return { success: false, error: 'WebSocket未连接' };
    }

    try {
      // 将所有元数据都放在metadata字段中，符合后端期望的格式
      const messageData = {
        type: 'message',
        content,
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString()
        }
      };

      // 添加到消息列表
      const userMessage: ChatMessage = {
        id: `msg_${Date.now()}`,
        type: 'user',
        content,
        timestamp: messageData.timestamp,
        sender: 'user'
      };
      setMessages(prev => [...prev, userMessage]);

      if (wsRef.current.readyState !== WebSocket.OPEN) {
        setError('WebSocket未连接');
        return { success: false, error: 'WebSocket未连接' };
      }

      wsRef.current.send(JSON.stringify(messageData));
      return { success: true, data: userMessage };
    } catch (error) {
      setError('发送消息失败');
      return { success: false, error: '发送消息失败' };
    }
  }, [isConnected, handleMessageByType]);

  // 发送系统消息
  const sendSystemMessage = useCallback((type: string, content: string, data: any = {}): { success: boolean; error?: string; data?: any } => {
    if (!isConnected || !wsRef.current) {
      setError('WebSocket未连接');
      return { success: false, error: 'WebSocket未连接' };
    }

    try {
      const messageData = {
        type,
        content,
        data,
        timestamp: new Date().toISOString()
      };

      wsRef.current.send(JSON.stringify(messageData));
      return { success: true, data: messageData };
    } catch (error) {
      setError('发送系统消息失败');
      return { success: false, error: '发送系统消息失败' };
    }
  }, [isConnected]);

  // TODO: 未来功能扩展 - 发送实时通知
  const sendNotification = useCallback((title: string, message: string, type: string = 'info'): { success: boolean; error?: string } => {
    // 这是未来实时通知需要实现
    // 发送实时通知到指定用户或用户组
    return { success: false, error: '功能待实现' };
  }, []);

  // 订阅项目更新
  const subscribeProject = useCallback((projectId: number): { success: boolean; error?: string } => {
    if (!isConnected || !wsRef.current) {
      setError('WebSocket未连接');
      return { success: false, error: 'WebSocket未连接' };
    }

    try {
      const messageData = {
        type: 'subscribe',
        subscription_type: 'project',
        subscription_id: projectId.toString(),
        timestamp: new Date().toISOString()
      };

      wsRef.current.send(JSON.stringify(messageData));
      return { success: true };
    } catch (error) {
      setError('订阅项目失败');
      return { success: false, error: '订阅项目失败' };
    }
  }, [isConnected]);

  // 取消订阅项目更新
  const unsubscribeProject = useCallback((projectId: number): { success: boolean; error?: string } => {
    if (!isConnected || !wsRef.current) {
      setError('WebSocket未连接');
      return { success: false, error: 'WebSocket未连接' };
    }

    try {
      const messageData = {
        type: 'unsubscribe',
        subscription_type: 'project',
        subscription_id: projectId.toString(),
        timestamp: new Date().toISOString()
      };

      wsRef.current.send(JSON.stringify(messageData));
      return { success: true };
    } catch (error) {
      setError('取消订阅项目失败');
      return { success: false, error: '取消订阅项目失败' };
    }
  }, [isConnected]);

  // TODO: 未来功能扩展 - 订阅进度更新
  const subscribeProgress = useCallback((taskId: string, callback: Function): Function => {
    // 这是未来进度更新需要实现
    // 订阅特定任务的进度更新
    return () => {
      // 取消订阅的逻辑
    };
  }, []);

  // TODO: 未来功能扩展 - 协作功能
  const joinCollaboration = useCallback((sessionId: string): { success: boolean; error?: string } => {
    // 这是未来协作功能需要实现
    // 加入协作会话
    return { success: false, error: '功能待实现' };
  }, []);

  // 注册消息回调
  const onMessage = useCallback((callback: (message: WebSocketMessage) => void): Function => {
    const id = Date.now();
    messageCallbacksRef.current.set(id, callback);
    return () => {
      messageCallbacksRef.current.delete(id);
    };
  }, []);

  // 注册状态变化回调
  const onStatusChange = useCallback((callback: (status: string) => void): Function => {
    const id = Date.now();
    statusChangeCallbacksRef.current.set(id, callback);
    return () => {
      statusChangeCallbacksRef.current.delete(id);
    };
  }, []);

  // 注册任务进度回调
  const onTaskProgress = useCallback((callback: (data: any) => void): Function => {
    const id = Date.now();
    taskProgressCallbacksRef.current.set(id, callback);
    return () => {
      taskProgressCallbacksRef.current.delete(id);
    };
  }, []);

  // 清除消息历史
  const clearMessages = useCallback(() => {
    setMessages([]);
    setLastMessage(null);
  }, []);

  // 清除错误
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // 获取连接状态信息
  const getConnectionInfo = useCallback(() => {
    return {
      status: connectionStatus,
      connected: isConnected,
      configFile,
      messageCount: messages.length,
      lastActivity: lastMessage?.timestamp || null,
      reconnectAttempts: reconnectAttemptsRef.current
    };
  }, [connectionStatus, isConnected, configFile, messages.length, lastMessage]);

  // 组件挂载时自动连接
  useEffect(() => {
    // 延迟连接，避免组件重复渲染导致多次连接
    const connectTimer = setTimeout(() => {
      connect();
    }, 100);

    return () => {
      clearTimeout(connectTimer);
      disconnect();
    };
  }, []); // 空依赖数组，只在组件挂载时执行一次

  // 心跳检测和连接保活
  useEffect(() => {
    let heartbeatInterval: NodeJS.Timeout;
    let connectionCheckInterval: NodeJS.Timeout;

    if (isConnected) {
      // 心跳检测 - 每30秒发送一次
      heartbeatInterval = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);

      // 连接状态检查 - 每60秒检查一次
      connectionCheckInterval = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
          // 重置连接状态，触发重连
          setIsConnected(false);
          setConnectionStatus('disconnected');
        }
      }, 60000);
    }

    return () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
      if (connectionCheckInterval) {
        clearInterval(connectionCheckInterval);
      }
    };
  }, [isConnected]);

  const value = useMemo(() => ({
    // 状态
    socket,
    isConnected,
    connectionStatus,
    lastMessage,
    messages,
    error,
    configFile,

    // 聊天功能
    sendMessage,
    sendSystemMessage,

    // 项目订阅功能
    subscribeProject,
    unsubscribeProject,

    // TODO: 未来功能占位
    sendNotification,     // 实时通知功能
    subscribeProgress,    // 进度更新功能
    joinCollaboration,    // 协作功能

    // 连接管理
    connect,
    disconnect,
    onMessage,
    onStatusChange,
    onTaskProgress,

    // 工具方法
    clearMessages,
    clearError,
    getConnectionInfo,
  }), [
    socket,
    isConnected,
    connectionStatus,
    lastMessage,
    messages,
    error,
    configFile,
    sendMessage,
    sendSystemMessage,
    subscribeProject,
    unsubscribeProject,
    sendNotification,
    subscribeProgress,
    joinCollaboration,
    connect,
    disconnect,
    onMessage,
    onStatusChange,
    onTaskProgress,
    clearMessages,
    clearError,
    getConnectionInfo,
  ]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}